import { useState, useEffect, useRef } from 'react';
import { ActionButton } from '../../common/Buttons/Buttons';
import { Header } from '../../common/Header/Header.jsx';
import { ComissionTable } from '../../common/Tables/Tables';
import { comissionsTableHeader } from '../../../assets/helpers/tables';
import { notify } from '../../../assets/helpers/toast';
import { sellerComissionService } from '../../../services/sellerComissionService';
import { rolesService } from '../../../services/rolesService';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import * as schemas from '../../../assets/schemas/schemas';
import { useHistory } from 'react-router-dom';
import { transcriptRoles } from '../../../assets/helpers/roles';
import { Input } from '../../common/Forms/Input/Input';
import moment from 'moment';
import { monthsComissionsOptions } from '../../../assets/helpers/options';
import {
  filterParams,
  filterOptionById,
} from '../../../assets/helpers/funciones';
import { LoadSpinner } from '../../common/LoadSpinner/LoadSpinner';
import './Comissions.scss';

export const Comissions = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schemas.filterRenewalMembership(t)),
  });

  const [yearOptions, setYearOptions] = useState([]);
  const [isLoading, setIsloading] = useState(false);
  const [rolesOptions, setRolesOptions] = useState([]);
  const tableRef = useRef(null);

  const [membershipsDatatable, setMembershipsDatatable] = useState({
    offset: 0, //Elementos que ya pasaron la pagina
    data: [],
    count: 0, //Cantidad de elementos
    page: 1,
    dateFrom: '',
    dateTo: '',
    isLoading: false,
    searchParam: '',
  });

  const preloadOptions = async (currentYear) => {
    let years = [];

    for (let i = currentYear + 1; i > 2000; i--) {
      years.push({
        name: i.toString(),
        _id: i.toString(),
      });
    }
    setYearOptions(years);
  };

  const getRoles = () => {
    rolesService
      .getRoles('Ventas')
      .then((response) => {
        if ([200, 201].includes(response?.status)) {
          let data = [...response?.data];
          for (let i = 0; i < data.length; i++) {
            data[i].name = transcriptRoles(data[i]?.name);
          }
          data = data.filter((role) =>
            ['Farmer', 'Hunter', 'Counter']?.includes(role.name)
          );
          setRolesOptions(data);
        } else {
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch(() => {});
  };

  const redirectToAffiliations = (commission) => {
    const { month, year } = commission;
    const from = moment(`${year}-${month}-01`)
      .startOf('month')
      .format('YYYY-MM-DD');
    const to = moment(`${year}-${month}-01`)
      .endOf('month')
      .format('YYYY-MM-DD');

    const newQueryParams = new URLSearchParams();
    newQueryParams.set('from', from);
    newQueryParams.set('to', to);
    newQueryParams.set('seller', commission.sellerId);
    // Abrir en una nueva pestaña
    window.open(`/dashboard/exportable?${newQueryParams.toString()}`, '_blank');
  };

  const preloadDates = () => {
    let currentYear = new Date()?.getFullYear();
    let currenMonth = moment(new Date()).month();
    let sinceYear = moment(new Date()).year();

    preloadOptions(currentYear).then((r) => {
      setTimeout(() => {
        setValue('sinceYear', sinceYear);
        setValue('sinceMonth', monthsComissionsOptions[currenMonth]?._id);
        getCommisionsList();
      }, 1000);
    });
  };

  useEffect(() => {
    getRoles();
    preloadDates();
  }, []);

  const getCommisionsList = (byMail) => {
    if (watch('sinceMonth') && watch('sinceYear')) {
      setMembershipsDatatable((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });

      let params = {
        month: watch('sinceMonth'),
        year: watch('sinceYear'),
        role: watch('role'),
        byMail: byMail,
      };

      if (!isLoading) {
        setIsloading(true);
        sellerComissionService
          .getComissionList(filterParams(params))
          .then((response) => {
            const groupedBySeller = {};
            response?.data?.sellerComission?.forEach((item) => {
              if (!groupedBySeller[item.sellerId]) {
                groupedBySeller[item.sellerId] = [];
              }
              groupedBySeller[item.sellerId].push(item);
            });
            const arrayBySeller = Object.keys(groupedBySeller).map(
              (seller, sellerId) => ({
                seller,
                role: groupedBySeller[seller][0]?.role,
                items: groupedBySeller[seller],
              })
            );
            if (response.status === 200) {
              if (byMail) {
                notify(
                  `Se ha enviado un correo con las comisiones de ${
                    filterOptionById(
                      monthsComissionsOptions,
                      parseFloat(watch('sinceMonth'))
                    )?.name
                  } ${watch('sinceYear')}`,
                  'success'
                );
              }
              if (!byMail) {
                setMembershipsDatatable((prevState) => {
                  return {
                    ...prevState,
                    count: response?.data?.total,
                    data: arrayBySeller,
                  };
                });
              }
              setIsloading(false);
            } else {
              notify(`${response.message}` || 'error', 'error');
              setIsloading(false);
            }
          })
          .catch(() => {
            setIsloading(false);
          });
      }
    }
  };

  useEffect(() => {
    getCommisionsList();
  }, [watch('sinceMonth'), watch('sinceYear'), watch('role')]);

  const recalculateComissions = () => {
    if (watch('sinceMonth') && watch('sinceYear')) {
      setMembershipsDatatable((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });

      let params = {
        month: watch('sinceMonth'),
        year: watch('sinceYear'),
        roles: [],
      };

      if (watch('role')) {
        params.roles = [watch('role')];
      } else {
        params.roles = rolesOptions.map((role) => role._id);
      }

      if (!isLoading) {
        setIsloading(true);
        sellerComissionService
          .recalculateComissionConfig(params)
          .then((response) => {
            if (response.status === 200) {
              notify(
                `Comisiones recalculadas con éxito para el mes de ${
                  filterOptionById(
                    monthsComissionsOptions,
                    parseFloat(watch('sinceMonth'))
                  )?.name
                } ${watch('sinceYear')}`,
                'success'
              );
              getCommisionsList();
              setIsloading(false);
            } else {
              notify(`${response.message}` || 'error', 'error');
              setIsloading(false);
            }
          })
          .catch(() => {
            setIsloading(false);
          });
      }
    }
  };

  return (
    <>
      <Header />
      <LoadSpinner show={isLoading} />
      <div className="service-with-container container">
        <div className="buttons-container">
          <h1 className="title-component">Comisiones Asesores</h1>
          <div style={{ display: 'flex' }}>
            <ActionButton
              label={'Metas'}
              action={() => history.push(`/config/goals`)}
            />
            <ActionButton
              label={'Exportar'}
              action={() => {
                getCommisionsList(true);
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ActionButton
            label={'Recalcular'}
            action={() => {
              recalculateComissions();
            }}
          />
        </div>
        <div
          className="exportables-buttons-container"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Input
            bootstrap={'col-12 col-md-1'}
            label={'Mes'}
            type={'select'}
            name={'sinceMonth'}
            options={monthsComissionsOptions}
            register={register}
            placeholder={''}
            errors={errors?.sinceMonth?.message}
          />
          <Input
            bootstrap={'col-12 col-md-1'}
            label={'Año'}
            type={'select'}
            name={'sinceYear'}
            options={yearOptions}
            register={register}
            placeholder={''}
            errors={errors?.sinceYear?.message}
          />{' '}
          <Input
            bootstrap={'col-12 col-md-1'}
            label={'Tipo de Asesor'}
            type={'select'}
            name={'role'}
            options={rolesOptions}
            register={register}
            placeholder={''}
            errors={errors?.role?.message}
          />{' '}
        </div>

        {membershipsDatatable?.data.map((item, index) => (
          <ComissionTable
            ref={tableRef}
            key={index}
            header={comissionsTableHeader}
            data={membershipsDatatable?.data[index]}
            redirectToAffiliations={redirectToAffiliations}
          />
        ))}

        {membershipsDatatable?.data?.length === 0 && (
          <>
            <h4 style={{ textAlign: 'center', marginTop: '24px' }}>
              No hay data disponible para el periodo seleccionado
            </h4>
          </>
        )}
      </div>
    </>
  );
};
